import {
  imageIntentGalleryFromDashboardGallery,
  NO_IMAGE_INTENT_GALLERY,
  tagWithImageIntentGallery,
} from '../shared/protectedImage/imageIntentGallery';

export const determinePriceListIdForOrder = (gallery) => {
  if (gallery.is_open_for_prepay) {
    return gallery.company_pricing_model_id;
  }

  if (
    gallery.reorder_buyer_pricing_model_id &&
    gallery.reorder_non_buyer_pricing_model_id
  ) {
    return gallery.gallery_buyer_status_at_ingest === 'buyer'
      ? gallery.reorder_buyer_pricing_model_id
      : gallery.reorder_non_buyer_pricing_model_id;
  }

  return (
    gallery.post_shoot_company_pricing_model_id ||
    gallery.company_pricing_model_id
  );
};

export const buildGalleryOrderShootData = ({ student, gallery, shootKey }) => ({
  student_id: student.id,
  // Both gallery types expose `password`: DP2 natively, Pics via
  // PicsGallerySerializer's password -> key alias
  shoot_key: shootKey || gallery.password,
  event_id: gallery.event_id || gallery.shoot_id,
  ...(gallery.pics_gallery_id == null && gallery.id != null
    ? { dp2_gallery_id: gallery.id }
    : {}),
  pics_gallery_id: gallery.pics_gallery_id,
  grade_code: 'IDK',
});

// The minimum a cart order needs to reload its own shoot later (ENG-2043):
// re-adding a declined bonus from the cart has to configure it in the shoot
// that awarded it, which may not be the last one the parent opened. Lookups
// stay by gallery KEY + student id — never by a bare gallery id.
export const buildShootLoadParamsForOrder = ({
  shoot,
  additionalData,
  selectedStudent,
}) => {
  const picsGalleryId = additionalData?.picsGalleryId ?? null;

  return {
    studentId: selectedStudent?.id ?? null,
    eventId: shoot?.event_id || shoot?.id || null,
    shootKey:
      additionalData?.picsGalleryPassword ||
      additionalData?.keySubmittedByUser ||
      shoot?.key ||
      null,
    picsGalleryId,
    dp2GalleryId:
      picsGalleryId == null
        ? additionalData?.galleryThatInitiatedOrder?.galleryId ?? null
        : null,
  };
};

// Same request shape buildGalleryOrderShootData produces, from the params
// stashed on the order instead of a dashboard gallery.
export const shootLoadParamsToRequest = (shootLoadParams) => {
  const params = shootLoadParams || {};

  return {
    student_id: params.studentId,
    shoot_key: params.shootKey,
    event_id: params.eventId,
    ...(params.picsGalleryId == null && params.dp2GalleryId != null
      ? { dp2_gallery_id: params.dp2GalleryId }
      : {}),
    pics_gallery_id: params.picsGalleryId ?? null,
    grade_code: 'IDK',
  };
};

// An order can only reload its shoot when it knows the student, the event and
// a gallery key. Orders persisted before `shootLoadParams` existed cannot.
export const canReloadShootForOrder = (order) => {
  const params = order?.shootLoadParams;

  return Boolean(
    params && params.studentId && params.eventId && params.shootKey,
  );
};

export const findDashboardGalleryForOrder = (
  dashboardData,
  studentId,
  galleryId,
) => {
  const dashboardStudent = (
    (dashboardData && dashboardData.students) ||
    []
  ).find((candidate) => String(candidate.id) === String(studentId));
  return (
    ((dashboardStudent && dashboardStudent.galleries) || []).find(
      (candidate) =>
        candidate.pics_gallery_id != null &&
        String(candidate.pics_gallery_id) === String(galleryId),
    ) || null
  );
};

const isPriceListMatch = (gallery, priceListIdForOrder) =>
  priceListIdForOrder != null &&
  [
    gallery.company_pricing_model_id,
    gallery.post_shoot_company_pricing_model_id,
    gallery.reorder_non_buyer_pricing_model_id,
    gallery.reorder_buyer_pricing_model_id,
  ].includes(priceListIdForOrder);

const galleryIdentity = (gallery) =>
  gallery.pics_gallery_id != null
    ? `pics:${gallery.pics_gallery_id}`
    : `gallery:${gallery.id}`;

const buildStudentPreviewImageUrl = (gallery) => {
  const firstImage = gallery && gallery.images && gallery.images[0];

  if (firstImage && firstImage.watermarked_preview_filename) {
    return `https://${process.env.IMAGE_PREVIEW_WATERMARKED_BUCKET}.s3.amazonaws.com/${firstImage.watermarked_preview_filename}`;
  }

  if (firstImage && firstImage.url) {
    return firstImage.url;
  }

  if (firstImage && firstImage.web_preview_filename) {
    return `https://${process.env.IMAGE_PREVIEW_BUCKET}.s3.amazonaws.com/${firstImage.web_preview_filename}`;
  }

  return null;
};

export const buildGalleryOrderContext = ({
  dashboardData,
  gallery,
  student,
}) => {
  const priceListIdForOrder = determinePriceListIdForOrder(gallery);
  const selectedGalleryImages = gallery.images || [];
  const otherGalleryImages = [];
  const selectedGalleryIdentity = galleryIdentity(gallery);
  // Group photos belong to the separate group gallery, whose key the page
  // does not have, so save-intent reports on them carry no gallery.
  const groupImages = tagWithImageIntentGallery(
    gallery.associated_group_images || [],
    NO_IMAGE_INTENT_GALLERY,
  );

  if (
    dashboardData &&
    dashboardData.show_price_list_matched_poses_in_order_ui
  ) {
    (dashboardData.students || []).forEach((dashboardStudent) => {
      (dashboardStudent.galleries || []).forEach((candidate) => {
        if (
          galleryIdentity(candidate) !== selectedGalleryIdentity &&
          isPriceListMatch(candidate, priceListIdForOrder)
        ) {
          // A pooled image is shown on another gallery's order screens, so it
          // carries its own gallery for save-intent attribution.
          otherGalleryImages.push(
            ...tagWithImageIntentGallery(
              candidate.images || [],
              imageIntentGalleryFromDashboardGallery(candidate),
            ),
          );
        }
      });
    });
  }

  return {
    allAvailablePosesForOrder: [
      ...selectedGalleryImages,
      ...otherGalleryImages,
    ],
    availablePoses: selectedGalleryImages,
    availableGroupImages: groupImages,
    galleryThatInitiatedOrder: {
      galleryId: gallery.id,
      studentId: student.id,
      buyerStatusAtIngest: gallery.gallery_buyer_status_at_ingest,
      priceListId: gallery.company_pricing_model_id,
      postEventPriceListId: gallery.post_shoot_company_pricing_model_id,
      reorderBuyerPriceListId: gallery.reorder_buyer_pricing_model_id,
      reorderNonBuyerPriceListId: gallery.reorder_non_buyer_pricing_model_id,
      priceListIdForOrder,
    },
    picsGalleryId: gallery.pics_gallery_id,
    // Student↔gallery assignment goes by gallery key (password), never by a
    // bare id — keep the key alongside the id wherever the id is stored
    picsGalleryPassword: gallery.password || null,
    studentGallery: selectedGalleryImages,
    studentGroupImages: groupImages,
    selectedStudent: student,
    student,
    studentId: student.id,
    studentPreviewImageUrl: buildStudentPreviewImageUrl(gallery),
    hideSelectedPosesInOrderUi:
      (dashboardData && dashboardData.hide_selected_poses_in_order_ui) || false,
    retouchingRequiresImageSelection:
      (dashboardData && dashboardData.retouching_requires_image_selection) ||
      false,
  };
};
